/* eslint-disable @typescript-eslint/no-explicit-any */
import ScrollModal from "@/components/common/scroll-modal";
import {
  Avatar,
  AvatarFallback,
  Badge,
  Button,
  Heading,
  Icon,
} from "@elaniintc/goes-design-system";
import { getDataTypeIcon, type Fields } from "./form-correction-table";
import dayjs from "dayjs";
import { buildFieldChangeHistory } from "./serialization/serialization";
import { useGetTableData } from "@/queries/data-center/data-center.api";
import { cn } from "@/utils/theme";
import type { FormDetailResponse } from "@/types/entities/formality-request";

interface FieldHistoryProps {
  isOpen: boolean;
  onClose: () => void;
  fieldSelected: Fields;
  applicant?: {
    fullName: string;
    date?: string | null;
  };
  formAssigment?: FormDetailResponse["form_assignments"][number];
}

const FieldHistoryModal = ({
  isOpen,
  onClose,
  fieldSelected,
  applicant,
  formAssigment,
}: FieldHistoryProps) => {
  const fieldHistory = buildFieldChangeHistory(fieldSelected, formAssigment);

  const formatDate = (date: Date) => {
    const d = dayjs(date);

    const day = d.format("D");
    const month = d.format("MMM");
    const rest = d.format(", YYYY | HH:mm");

    return `el ${day} de ${month.charAt(0).toUpperCase() + month.slice(1)}${rest}`;
  };

  return (
    <ScrollModal
      isOpen={isOpen}
      onClose={onClose}
      modalContentClassName="gap-0 max-h-[680px]"
      modalChildrenClassName="rounded-b-2xl"
      header={
        <div className="px-8 py-3">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2">
              <Avatar className="shrink-0" shape="square" size="2xs">
                <AvatarFallback color="neutral" shape="square">
                  {getDataTypeIcon(fieldSelected.type)}
                </AvatarFallback>
              </Avatar>

              <div className="text-center text-h2! font-semibold text-heading">
                <Heading variant="h5">{fieldSelected.label}</Heading>
              </div>
            </div>
            <Button
              variant="quiet"
              onClick={onClose}
              className="h-9 w-9 justify-center items-center gap-2 rounded-lg border border-neutral shadow-sm"
              data-testid="close-new-notification-modal-button"
            >
              <Icon type="far" name="times" className="text-sm text-body" />
            </Button>
          </div>
        </div>
      }
    >
      <div className="px-8 pt-8">
        {/* Empty State */}

        {fieldHistory.length ? (
          <>
            {fieldHistory.map((field) => {
              return (
                <div
                  key={`change-${field.oldFieldId}-${field.newFieldId}-${field.changedAt.getTime()}`}
                  className="flex flex-col gap-8 border border-neutral-quiet rounded-xl mb-8"
                >
                  <div className="p-3 flex flex-col gap-3">
                    <div className="flex items-center gap-1">
                      <Avatar className="shrink-0" size="2xs">
                        <AvatarFallback color="neutral">
                          <Icon type="fa" size="xs" name="file-pen" />
                        </AvatarFallback>
                      </Avatar>

                      <div className="font-semibold text-heading">
                        <span>Revisión {field.order}</span>
                      </div>
                    </div>

                    <div className="rounded-lg bg-neutral-object-accent-silent border border-neutral-quiet p-2 flex flex-col gap-2">
                      <div className="flex items-center gap-2">
                        <Icon
                          type="fa"
                          name="comment"
                          className="text-warning-quiet"
                        />
                        <span className="text-warning">Comentario: </span>
                      </div>

                      <span className="text-body">
                        {field.comment || "No hay comentario para este cambio."}
                      </span>

                      <div className="flex items-center gap-1 mb-1 text-sm">
                        <span className="text-body">Por</span>

                        <Avatar className="shrink-0" size="xs">
                          <AvatarFallback color="action">
                            {field?.author?.substring(0, 1).toUpperCase() ||
                              "U"}
                          </AvatarFallback>
                        </Avatar>

                        <span className="text-action">{field?.author}</span>

                        <span className="text-body">
                          {formatDate(field?.changedAt)}
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-col gap-2">
                      <span className="text-body-loud font-bold">
                        Valor que cambió
                      </span>
                      <div className="flex flex-col gap-2 rounded-lg bg-neutral-object-accent-silent border border-neutral-quiet p-2">
                        <div className="flex items-center gap-2">
                          <Icon
                            type="fa"
                            name="arrow-down"
                            className="text-body-quiet"
                          />
                          <div className="flex flex-col gap-1">
                            <FieldValue
                              field={fieldSelected}
                              value={field.oldValue}
                              filledFieldId={field.oldFieldId}
                              isOldValue
                            />
                            <FieldValue
                              field={fieldSelected}
                              filledFieldId={field.newFieldId}
                              value={field.newValue}
                            />
                          </div>
                        </div>

                        <div className="flex items-center gap-1 mb-1 text-sm">
                          <span className="text-body">Por</span>

                          <Avatar className="shrink-0" size="xs">
                            <AvatarFallback color="action">
                              {applicant?.fullName
                                .substring(0, 1)
                                .toUpperCase() || "U"}
                            </AvatarFallback>
                          </Avatar>

                          <span className="text-action">
                            {applicant?.fullName}
                          </span>

                          <span className="text-body">
                            {formatDate(
                              applicant?.date
                                ? new Date(applicant.date)
                                : new Date(),
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </>
        ) : (
          <div className="h-full min-h-[520px] flex flex-col justify-center items-center gap-4 p-8">
            <Heading variant="h2" className="text-center">
              No hay cambios dentro de este campo.
            </Heading>
            <span className="text-body text-center">
              Todavía no se han recibido comentarios ni generado cambios sobre
              este campo.
            </span>
          </div>
        )}
      </div>
    </ScrollModal>
  );
};

export default FieldHistoryModal;

const FieldValue = ({
  field,
  value,
  isOldValue,
  filledFieldId,
}: {
  filledFieldId: string;
  field: Fields;
  value: any;
  isOldValue?: boolean;
}) => {
  if (field.type === "interoperability") {
    field.config.input_field.map(() => {});
    return (
      <div className="flex flex-col gap-1">
        {field.config.input_field.map(
          ({ label, name }: { name: string; label: string }) => (
            <div key={`${filledFieldId}- ${name}`} className="flex flex-col">
              <span className="text-xs text-info font-semibold">{label}:</span>
              <span
                className={cn(
                  "text-body",
                  isOldValue && "line-through text-on-disabled",
                )}
              >
                {String(value[name] ?? "—")}
              </span>
            </div>
          ),
        )}
      </div>
    );
  }

  if (field.type === "datetime_range" || field.type === "date_range") {
    const start = value?.start || "—";
    const end = value?.end || "—";
    return (
      <span
        className={cn(
          "text-body",
          isOldValue && "line-through text-on-disabled",
        )}
      >
        {start} {" -> "} {end}
      </span>
    );
  }

  if (field.type === "dui_nit" && field.config?.allow_select_document_type) {
    return (
      <span
        className={cn(
          "text-body",
          isOldValue && "line-through text-on-disabled",
        )}
      >
        ({value.type === "passport" ? "PASAPORTE" : value.type.toUpperCase()}
        )&nbsp;{value.value}
      </span>
    );
  }

  if (field.type === "select" && field.config?.options) {
    if (Array.isArray(value)) {
      const selectedOptions = (
        field.config.options as {
          label: string;
          value: string;
        }[]
      ).filter((option) => value.includes(option.value));
      return selectedOptions.map((option) => option.label).join(", ");
    }
  }

  if (field.type === "select" && field?.config.options_type === "database") {
    if (Array.isArray(value)) {
      return (
        <div className="flex gap-1">
          {value.map((val, index) => {
            return (
              <div key={index}>
                <DataCenterValue
                  table_name={field.config.data_center_table}
                  column_name={field.config.data_center_column}
                  table_source={field.config.source}
                  value={val as string}
                  oldValue={isOldValue}
                />
                {index < value.length - 1 && (
                  <span className="text-body">, </span>
                )}
              </div>
            );
          })}
        </div>
      );
    }
    return (
      <DataCenterValue
        table_name={field.config.data_center_table}
        column_name={field.config.data_center_column}
        table_source={field.config.source}
        value={value as string}
        oldValue={isOldValue}
      />
    );
  }

  if (field.type === "switch") {
    return (
      <span
        className={cn(
          "text-body",
          isOldValue && "line-through text-on-disabled",
        )}
      >
        {value ? "Sí" : "No"}
      </span>
    );
  }
  if (field.type === "file") {
    const files = field?.filled_fields?.find(
      (ff) => ff.id === filledFieldId,
    )?.files;

    if (files && Array.isArray(files)) {
      return (
        <div className="flex flex-wrap gap-2">
          {files.map((file) => (
            <FileValue key={file.url} file={file} isOldValue={isOldValue} />
          ))}
        </div>
      );
    }
  }

  if (Array.isArray(value)) {
    return (
      <span
        className={cn(
          "text-body",
          isOldValue && "line-through text-on-disabled",
        )}
      >
        {value.join(", ") || "-"}
      </span>
    );
  }

  return (
    <span
      className={cn("text-body", isOldValue && "line-through text-on-disabled")}
    >
      {value ?? "—"}
    </span>
  );
};

const DataCenterValue = ({
  value,
  table_name,
  column_name,
  table_source,
  oldValue,
}: {
  table_name: string;
  column_name: string;
  table_source: string;
  value?: string | number | string[];
  oldValue?: boolean;
}) => {
  const { data, error } = useGetTableData(table_name!, table_source, 1, [
    {
      name: "id",
      value: value as string,
      operator: "EQUALS",
      type: "STRING",
    },
  ]);
  if (!data && !error)
    return <span className="text-body">{"Cargando..."}</span>;

  if (error) return <span className="text-body">{"Error cargando datos"}</span>;

  const record = data?.data?.data?.[0];
  if (record) {
    return (
      <span
        className={cn("text-body", oldValue && "line-through text-on-disabled")}
      >
        {record[column_name as keyof typeof record] as string}
      </span>
    );
  }

  return <span className="text-body">{"—"}</span>;
};

const FileValue = ({
  file,
  isOldValue,
}: {
  file: { url: string; file_name: string };
  isOldValue?: boolean;
}) => {
  if (file.url) {
    return (
      <Badge
        key={file.url}
        variant="neutral"
        className="max-w-90 cursor-pointer"
        onClick={() => window.open(file.url, "_blank", "noopener,noreferrer")}
      >
        <span
          className={cn(
            "truncate flex-1 min-w-0",
            isOldValue && "line-through text-on-disabled",
          )}
        >
          {file?.file_name}
        </span>
        <Icon type="fas" name="file" size="xs" className="shrink-0" />
      </Badge>
    );
  }
};
